import { useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { orderBy, sortBy } from 'lodash-es';
import { useGrowers } from '../../hooks/use-growers';
import { FilterSelector } from '../FilterSelector';
import { FarmgateLink } from './FarmgateLink';

export function GrowerSelector() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    all: allGrowers,
    selected: selectedGrower,
    selectGrower,
    organizations,
    selectedOrganization,
    selectOrganization,
    resetGrowerSelection,
  } = useGrowers();

  const growers = useMemo(() => {
    if (selectedOrganization) {
      return sortBy(allGrowers, (grower) => grower.operationName.toUpperCase());
    }
  }, [allGrowers, selectedOrganization]);

  useEffect(() => {
    if (allGrowers?.length === 1) {
      selectGrower(allGrowers[0].guid);
    }

    const selectedOrganizationGrowers = allGrowers?.filter(
      (grower) => grower.organizationId === selectOrganization.id,
    );

    if (selectedOrganizationGrowers?.length === 1) {
      selectGrower(selectedOrganizationGrowers[0].guid);
    }
  }, [allGrowers, organizations, selectGrower, selectOrganization]);

  useEffect(() => {
    const defaultOrganization = organizations?.find(
      (organization) => organization?.isDefault,
    );

    if (defaultOrganization) {
      selectOrganization(defaultOrganization);
    }

    if (organizations?.length === 1) {
      selectOrganization(organizations[0]);
    }
  }, [organizations, selectOrganization]);

  function handleChange(grower) {
    selectGrower(grower.guid);
  }

  function handleOrganizationChange(organization) {
    resetGrowerSelection();
    selectOrganization(organization);

    if (selectedOrganization?.id) {
      navigate('/');
    }
  }

  const isVisible = shouldShow();

  return (
    <>
      <FilterSelector
        isVisible={isVisible && organizations?.length > 1}
        selectedOption={selectedOrganization || null}
        options={organizations}
        onChange={handleOrganizationChange}
        sortKeys={['name']}
        sorter={(items) =>
          items
            ? orderBy(items, ['item.isDefault', 'item.name'], ['desc', 'asc'])
            : items
        }
        optionLabelKey="name"
        optionKey="id"
        optionContentKey="name"
        inputLabel="Organization"
      />
      <FilterSelector
        isVisible={isVisible && selectedOrganization?.id}
        selectedOption={selectedGrower}
        options={growers}
        onChange={handleChange}
        sortKeys={['operationName']}
        optionLabelKey="operationName"
        optionKey="guid"
        optionContentKey="operationName"
        inputLabel="Operation"
        InputLabelProps={{ shrink: true }}
      />
      <FarmgateLink isVisible={isVisible} />
    </>
  );

  function shouldShow() {
    const isAdminPage =
      /equipment-management|grower-management|organization-management|user-management|operation-management|soil-sample-order-|pending-fields/i.test(
        location.pathname,
      );

    return !!allGrowers?.length && !isAdminPage;
  }
}
